import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import copy from 'copy-to-clipboard';
import { Popover } from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import publicationService from 'services/PublicationService';

import messages from './messages';
import { StatusChip, Content, Status, Section, Icon } from './styles';
import PopoverStatusInfo from './PopoverStatusInfo';

const PublishStatus = ({ itemIndex, publication, publishStatusInfo, theme }) => {
  const referenceNode = useRef();
  const popUpRef = useRef(false);
  const [showPopUp, setShowPopUp] = useState(false); // State to trigger re-render for Popover
  const [oculusVersion, setOculusVersion] = useState(null);

  const { etextVersion, bookId, jobId } = publication;
  const { status, statusMessage, failureStatusCodes } = publishStatusInfo;

  useEffect(() => {
    if (popUpRef.current && itemIndex === 0 && bookId) {
      _fetchOculusStatus(bookId);
    }
  }, [itemIndex, bookId]);

  const _fetchOculusStatus = async (bookId) => {
    if (bookId) {
      try {
        const { oculusStatus } = await publicationService.getOculusStatus(bookId);
        setOculusVersion(oculusStatus);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch Oculus status:', err);
        setOculusVersion('Error fetching status');
      }
    }
  };

  const onMouseEnter = () => {
    popUpRef.current = true;
    setShowPopUp(true);

    if (itemIndex === 0 && bookId) {
      _fetchOculusStatus(bookId);
    }
  };

  const onMouseLeave = () => {
    popUpRef.current = false;
    setShowPopUp(false);
  };

  const copyBookId = (bookId) => () => {
    copy(bookId);
  };

  const getStatus = (status) => intl.formatMessage(messages[status] || messages['UNKNOWN']);

  let statusInfo = {
    COMPLETED: {
      content: (
        <Content data-testid="success-info">
          {etextVersion && <Section> Oculus Version : {etextVersion}</Section>}
          {itemIndex === 0 ? <Section> Oculus Status : {oculusVersion || 'Getting Info...'}</Section> : null}
          {bookId && <Section onClick={copyBookId(bookId)}>
            Book ID : {bookId} <Icon fill={theme['color.primary']} icon='copy' />
          </Section>}
          <Section> Job ID : {jobId} </Section>
        </Content>
      ),
      icon: null,
    },
    FAILED: {
      content: (
        <Content data-testid="failure-info">
          {failureStatusCodes.length > 1 ? (
            <ul>
              {failureStatusCodes.map((code) => (
                <li key={code.errorStatusCode}>
                  {messages?.[code?.errorStatusCode]
                    ? intl.formatMessage(messages[code.errorStatusCode])
                    : intl.formatMessage(messages.UNKNOWN)}
                </li>
              ))}
            </ul>
          ) : (
            <Section>{getStatus(statusMessage)}</Section>
          )}
          <Section> Job ID : {jobId} </Section>
        </Content>
      ),
      icon: <Icon fill={theme['color.redDark']} icon='warningCircle' />,
    },
    PEARSONPLUS_TITLE_INGEST_COMPLETE: {
      content: (
        <PopoverStatusInfo
          etextVersion={etextVersion}
          itemIndex={itemIndex}
          oculusVersion={oculusVersion}
          statusMessage={statusMessage}
          bookId={bookId}
          copyBookId={copyBookId}
          theme={theme}
          jobId={jobId}
        />
      ),
      icon: null,
    },
    INDEX_IN_PROGRESS: {
      content: (
        <PopoverStatusInfo
          etextVersion={etextVersion}
          itemIndex={itemIndex}
          oculusVersion={oculusVersion}
          statusMessage={statusMessage}
          bookId={bookId}
          copyBookId={copyBookId}
          theme={theme}
          jobId={jobId}
        />

      ),
      icon: null,
    },
  }[status];

  if(!statusInfo) {
    statusInfo = {
      content: (
        <Content data-testid="inprogress-info">
          <Section> Job ID : {jobId} </Section>
        </Content>
      ),
      icon: null,
    };
  }


  return (
    <Status data-testid="status-chip" ref={referenceNode} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <StatusChip status={status}>{getStatus(status)}</StatusChip>
      {statusInfo?.icon}
      {( // Use state to trigger Popover rendering
        createPortal(
          <Popover
            data-testid="popover"
            popperOptions={{ placement: 'left' }}
            referenceNode={referenceNode.current}
            show={showPopUp}
            onClose={() => {
              popUpRef.current = false;
              setShowPopUp(false);
            }}
          >
            {statusInfo.content}
          </Popover>, document.body)
      )}
    </Status>
  );
};

PublishStatus.propTypes = {
  itemIndex: PropTypes.number,
  publication: PropTypes.object,
  publishStatusInfo: PropTypes.object,
  theme: PropTypes.object,
};

export default withTheme(PublishStatus);
